import React, { useRef, useState, useEffect } from "react";
import { Box, Tooltip, useMediaQuery } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { PublicServiceRequest } from "../../../serviceBoard/models/models";
import { ServiceTypeCode } from "../../models/models";

const DESC_LINE_HEIGHT = 1.65;

interface RfsIssueCardProps {
  sr: PublicServiceRequest;
  affectedPartName?: string;
}

const RfsIssueCard = ({ sr, affectedPartName }: RfsIssueCardProps) => {
  const { translate } = useTranslation(["serviceBoard", "servicing"]);
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(max-width:1200px)");

  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [isDescClamped, setIsDescClamped] = useState(false);
  const descRef = useRef<HTMLDivElement>(null);

  const maxLines = isPhone ? 3 : isTablet ? 4 : 6;

  useEffect(() => {
    const el = descRef.current;
    if (!el || isDescExpanded) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (descRef.current) {
          setIsDescClamped(descRef.current.scrollHeight > descRef.current.clientHeight + 1);
        }
      });
    });
  });

  const getServiceTypeLabel = () => {
    switch (sr.serviceTypeCode) {
      case ServiceTypeCode.onSiteRepair:
        return translate("serviceBoard:serviceTypeRepair");
      case ServiceTypeCode.maintenance:
        return translate("serviceBoard:serviceTypeMaintenance");
      case ServiceTypeCode.partsReplacement:
        return translate("serviceBoard:serviceTypeParts");
      default:
        return null;
    }
  };

  const serviceTypeLabel = getServiceTypeLabel();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Issue title — largest text */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: isPhone ? "16px" : isTablet ? "18px" : "20px",
          fontWeight: 700,
          color: "#1E293B",
          lineHeight: 1.3,
        }}
      >
        {sr.issue || "\u2014"}
      </Box>

      {/* Service type + component chips */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {serviceTypeLabel && (
          <Tooltip title={translate("servicing:rfsDetail.serviceType")} arrow>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: dt.blueSoft,
                color: dt.blue,
                borderRadius: "12px",
                padding: "3px 12px",
                fontSize: "12px",
                fontWeight: 600,
                fontFamily: dt.font,
                whiteSpace: "nowrap",
              }}
            >
              {serviceTypeLabel}
            </Box>
          </Tooltip>
        )}
        {/* Affected machine part chip — resolved from machineParts data */}
        {affectedPartName && (
          <Tooltip title={translate("servicing:rfsDetail.affectedMachinePart")} arrow>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "#FFF7ED",
                color: "#C2410C",
                borderRadius: "12px",
                padding: "3px 12px",
                fontSize: "12px",
                fontWeight: 600,
                fontFamily: dt.font,
                whiteSpace: "nowrap",
              }}
            >
              <BuildCircleOutlinedIcon sx={{ fontSize: 14 }} />
              {affectedPartName}
            </Box>
          </Tooltip>
        )}
        {sr.alarmCode && (
          <Tooltip title={translate("servicing:rfsDetail.alarmCode")} arrow>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "#FEF2F2",
                color: "#DC2626",
                borderRadius: "12px",
                padding: "3px 12px",
                fontSize: "12px",
                fontWeight: 600,
                fontFamily: dt.font,
                whiteSpace: "nowrap",
              }}
            >
              <WarningAmberRoundedIcon sx={{ fontSize: 14 }} />
              {sr.alarmCode}
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Description */}
      {sr.description && (
        <Box>
          <Box
            ref={descRef}
            sx={{
              fontSize: "14px",
              fontFamily: dt.font,
              fontWeight: 400,
              lineHeight: DESC_LINE_HEIGHT,
              color: dt.textSecondary,
              position: "relative",
              ...(!isDescExpanded && {
                maxHeight: `calc(${maxLines} * ${DESC_LINE_HEIGHT}em)`,
                overflow: "hidden",
              }),
              ...(!isDescExpanded && isDescClamped && {
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "28px",
                  background: `linear-gradient(to bottom, transparent, ${dt.pageBg})`,
                  pointerEvents: "none",
                },
              }),
            }}
          >
            {sr.description}
          </Box>
          {(isDescClamped || isDescExpanded) && (
            <Box
              component="span"
              onClick={() => setIsDescExpanded((prev) => !prev)}
              sx={{
                display: "inline-block",
                marginTop: "4px",
                fontSize: "12px",
                fontFamily: dt.font,
                fontWeight: 600,
                color: dt.blue,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {isDescExpanded
                ? translate("servicing:rfsDetail.showLess")
                : translate("servicing:rfsDetail.showMore")}
            </Box>
          )}
        </Box>
      )}

      {/* Notes — text section below description */}
      {sr.notes && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            backgroundColor: "#F8FAFC",
            borderRadius: "8px",
            padding: "10px 12px",
            borderLeft: `3px solid ${dt.blue}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: "12px",
              fontFamily: dt.font,
              fontWeight: 600,
              color: dt.textSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            <NotesRoundedIcon sx={{ fontSize: 14, color: dt.textSecondary }} />
            {translate("servicing:rfsDetail.notes")}
          </Box>
          <Box
            sx={{
              fontSize: "14px",
              fontFamily: dt.font,
              fontWeight: 400,
              lineHeight: 1.6,
              color: dt.textPrimary,
              whiteSpace: "pre-wrap",
            }}
          >
            {sr.notes}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RfsIssueCard;
